import { useSelector } from 'react-redux';

const STORAGE_KEY = 'orders';

// Abandoned orders are dropped after a week so they do not linger in the
// browser indefinitely.
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;

const isFresh = ({ updatedAt } = {}) =>
  typeof updatedAt === 'number' && Date.now() - updatedAt < MAX_AGE;

/**
 * Read the stored orders, dropping every scope that has expired.
 *
 * Entries written before this format existed carry no `updatedAt` and are
 * dropped as well, which clears them from the browser on first use.
 *
 * @param {Storage} storage The storage to read from.
 *
 * @returns {Object} The stored orders per scope.
 */
const read = (storage) => {
  const stored = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}');
  const data = Object.fromEntries(Object.entries(stored).filter(([, entry]) => isFresh(entry)));

  if (Object.keys(data).length !== Object.keys(stored).length) {
    storage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  return data;
};

/**
 * Discard the order data that may not outlive the session.
 *
 * Session storage is bound to the browser tab rather than to the session, so
 * logging out only navigates the page and leaves the stored WHOIS contacts
 * behind for anyone with access to that tab. The order itself lives in local
 * storage and is kept, so it can still be picked up after logging back in.
 */
export const clearPrivateOrders = () => {
  window.sessionStorage.removeItem(STORAGE_KEY);
};

export const useOrderStorage = (name, session) => {
  const customerId = useSelector(({ app }) => app.customer.id);
  const userId = useSelector(({ user }) => user.id);

  // Scoped per user so a shared browser never exposes one user's order to the
  // next, and per customer so switching customers keeps the orders apart.
  const scope = `${userId}:${customerId}`;
  const storage = session ? window.sessionStorage : window.localStorage;

  return {
    get: () => read(storage)[scope]?.values?.[name] ?? [],
    set: (value) => {
      const data = read(storage);

      data[scope] = {
        updatedAt: Date.now(),
        values: { ...data[scope]?.values, [name]: value },
      };

      storage.setItem(STORAGE_KEY, JSON.stringify(data));
    },
  };
};
